import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "VARfacts",
      "url": "https://varfacts.com",
      "description": "Record-based evaluation and verification-status reporting for digital asset record sets."
    },
    {
      "@type": "WebPage",
      "name": "Virtual Asset Registry (VAR) Framework",
      "description": "A structured methodology for the documentation-status review, provenance evaluation, and reconciliation of digital asset record sets.",
      "publisher": { "@type": "Organization", "name": "VARfacts" }
    }
  ]
};

export default function VarFrameworkPage() {

  useEffect(() => {
    document.title = 'Virtual Asset Registry (VAR) Framework — VARfacts';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(JSON_LD);
    document.head.appendChild(script);
    return () => {
      script.remove();
      document.title = 'VARfacts | Source-Cited Digital Asset Record Review for Family Law and Estate Matters';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="var-framework-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#D4D4D0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/virtual-asset-registry-framework" className="nav-link text-sm">Framework</Link>
            <Link to="/family-law-digital-assets" className="nav-link text-sm">Family Law</Link>
            <Link to="/estate-digital-asset-records" className="nav-link text-sm">Estates</Link>
            <Link to="/contact" className="nav-link text-sm">Contact</Link>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild className="btn-gold px-5 py-2 rounded-sm text-xs">
              <a href="mailto:secure.intake@varfacts.com">Contact Us</a>
            </Button>
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <article className="max-w-3xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-32 md:pb-24">

        {/* H1 + Subheading */}
        <header className="space-y-4 mb-16" data-testid="framework-header">
          <h1 className="font-heading text-4xl sm:text-5xl text-[#0B1F3F] leading-tight tracking-tight" data-testid="framework-h1">
            Virtual Asset Registry (VAR) Framework
          </h1>
          <p className="text-sm text-[#94A3B8]">
            Version 1.0 &mdash; Public Exposure Draft &nbsp;|&nbsp; Issued by VARfacts &nbsp;|&nbsp; Effective 2026
          </p>
          <div className="w-16 h-0.5 bg-[#C5A55A]" />
        </header>

        {/* Section 1 — Introduction */}
        <section className="mb-14 space-y-4" data-testid="framework-introduction">
          <h2 className="font-heading text-2xl text-[#0B1F3F]">Introduction</h2>
          <Separator className="bg-[#D4D4D0]" />
          <p className="text-[#64748B] leading-relaxed">
            The Virtual Asset Registry (VAR) Framework is a structured methodology for the verification-status review, provenance evaluation, and reconciliation of digital asset record sets. It was developed to support consistent, source-cited evaluation of fragmented digital asset materials in legal, insurance, fiduciary, investigative, and dispute-related matters.
          </p>
          <p className="text-[#64748B] leading-relaxed">
            The framework is published as a public reference methodology. It is not a certification program, a regulatory standard, or a software tool. VARfacts applies the VAR Framework in producing source-cited verification-status reports.
          </p>
        </section>

        {/* Section 2 — Download the Framework */}
        <section className="mb-14 space-y-4" data-testid="framework-download">
          <h2 className="font-heading text-2xl text-[#0B1F3F]">Download the Framework</h2>
          <Separator className="bg-[#D4D4D0]" />
          <div className="pt-2">
            <Button asChild variant="outline" className="btn-secondary-varfacts rounded-sm px-5 py-2.5 text-sm" data-testid="framework-download-btn">
              <a href="/api/documents/VAR_Framework_v1.0_Public_Exposure_Draft.pdf">
                Download VAR Framework v1.0 (PDF)
              </a>
            </Button>
          </div>
          <p className="text-sm text-[#94A3B8] leading-relaxed">
            Virtual Asset Registry (VAR) Framework v1.0, Public Exposure Draft. Version-controlled and freely available for reference by counsel, fiduciaries, and institutional reviewers.
          </p>
        </section>

        {/* Section 3 — Suggested Citation */}
        <section className="mb-14 space-y-4" data-testid="framework-citation">
          <h2 className="font-heading text-2xl text-[#0B1F3F]">Suggested Citation</h2>
          <Separator className="bg-[#D4D4D0]" />
          <div className="border border-[#D4D4D0] rounded-sm p-6 bg-[#F5F3EF]">
            <p className="text-[#0B1F3F] leading-relaxed">
              Virtual Asset Registry (VAR) Framework v1.0 (Public Exposure Draft), VARfacts, 2026.
            </p>
            <p className="text-[#0B1F3F] leading-relaxed">
              Available at: varfacts.com/virtual-asset-registry-framework
            </p>
          </div>
        </section>

        {/* Section 4 — What the Framework Evaluates */}
        <section className="mb-14 space-y-4" data-testid="framework-evaluates">
          <h2 className="font-heading text-2xl text-[#0B1F3F]">What the Framework Evaluates</h2>
          <Separator className="bg-[#D4D4D0]" />
          <p className="text-[#64748B] leading-relaxed">
            The VAR Framework applies a three-part methodology:
          </p>
          <div className="space-y-6 pt-2">
            <div className="space-y-1">
              <p className="text-[#0B1F3F] font-semibold">Attribution Review</p>
              <p className="text-[#64748B] leading-relaxed">
                Evaluation of whether produced records support an asserted relationship between a party, account, wallet address, or associated activity.
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-[#0B1F3F] font-semibold">Provenance Mapping</p>
              <p className="text-[#64748B] leading-relaxed">
                Timeline reconstruction to identify gaps, discontinuities, and unsupported transitions.
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-[#0B1F3F] font-semibold">Record Reconciliation</p>
              <p className="text-[#64748B] leading-relaxed">
                Structured comparison of independent record sources to evaluate consistency, completeness, and unresolved conflicts.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5 — What the Framework Is Not */}
        <section className="mb-14 space-y-4" data-testid="framework-is-not">
          <h2 className="font-heading text-2xl text-[#0B1F3F]">What the Framework Is Not</h2>
          <Separator className="bg-[#D4D4D0]" />
          <p className="text-[#64748B] leading-relaxed">
            The VAR Framework does not provide legal, tax, financial, or investment advice. It does not determine ownership, attest to asset value, or guarantee authenticity. Conformance language does not constitute a legal certification, audit opinion, or attestation. All findings are limited to the produced record set.
          </p>
        </section>

        {/* Section 6 — Related Materials */}
        {/* Section 7 — Contact */}
        <section className="mb-8 space-y-4" data-testid="framework-contact">
          <h2 className="font-heading text-2xl text-[#0B1F3F]">Contact</h2>
          <Separator className="bg-[#D4D4D0]" />
          <p className="text-[#64748B] leading-relaxed">
            For questions about applying this framework to a specific matter:
          </p>
          <p>
            <a href="mailto:secure.intake@varfacts.com" className="text-[#0B1F3F] font-semibold hover:text-[#C5A55A] transition-colors">
              secure.intake@varfacts.com
            </a>
          </p>
        </section>

        {/* Disclaimer */}
        <footer className="pt-4 border-t border-[#D4D4D0]">
          <p className="text-xs text-[#94A3B8] italic leading-relaxed">
            VARfacts provides record-based evaluation and verification-status reporting only. VARfacts does not provide legal, tax, or investment advice, and does not provide custody services.
          </p>
          <p className="text-xs text-[#94A3B8] mt-4">&copy; 2026 VARfacts. All Rights Reserved.</p>
        </footer>
      </article>

      {/* Site Footer */}
      <footer className="bg-[#0B1F3F] py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5 space-y-4">
              <img src="/01_VARfacts_logo_white_transparent.png" alt="VARfacts" className="h-8 opacity-90" />
            </div>
            <div className="md:col-span-3 space-y-3">
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Links</p>
              <div className="space-y-2">
                <Link to="/" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Home</Link>
                <Link to="/virtual-asset-registry-framework" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Framework</Link>
                <Link to="/family-law-digital-assets" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Family Law</Link>
                <Link to="/estate-digital-asset-records" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Estates</Link>
                <Link to="/contact" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Contact</Link>
              </div>
            </div>
            <div className="md:col-span-4 space-y-3">
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Contact</p>
              <div className="space-y-2">
                <p className="text-white/60 text-sm">secure.intake@varfacts.com</p>
                <p className="text-white/60 text-sm">Nashville, Tennessee</p>
                <a href="https://www.linkedin.com/company/varfacts" target="_blank" rel="noopener noreferrer" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">LinkedIn</a>
              </div>
            </div>
          </div>
          <Separator className="my-8 bg-white/10" />
          <p className="text-white/20 text-xs">&copy; 2026 VARfacts. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}
